import { Flex } from 'antd'
import { Checkbox, Link, T } from 'govwell-ui'
import isNil from 'lodash.isnil'
import { computed, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import EmptyState from 'src/components/EmptyState'
import DateFilter from 'src/components/TableViews/DateFilter'
import FilterTypeSelect from 'src/components/TableViews/FilterTypeSelect'
import NumberFilter from 'src/components/TableViews/NumberFilter'
import OptionCheckbox from 'src/components/TableViews/OptionCheckbox'
import { useTableViewConfig } from 'src/components/TableViews/TableViewConfigContext'
import Text from 'src/components/Typography/Text'
import useDisclosure from 'src/hooks/use-disclosure'
import { TableColumnTemplate } from 'src/models/TableViews/TableColumnTemplate'

import { useMyOrg } from '../../layouts/AppStateContextLayout/utils'
const StyledFlex = styled(Flex)`
  padding: 0px 12px;
`

type Props = {
  columnTemplate: TableColumnTemplate
}
const FilterEditor = ({ columnTemplate }: Props) => {
  const { filterManager } = useTableViewConfig()
  const paymentProviderFormatted = useMyOrg()?.paymentProviderFormatted || ''
  const { isOpen: isArchivedOptionsListOpen, open: openArchivedOptionsList } = useDisclosure()

  // Mirrors AddFilterButton's guard. The two are only ever invoked from
  // chip rows or the add-filter dropdown, both of which already gate on
  // `filterManager` being non-null at the parent — but we re-check here
  // so a future caller can't render a `FilterEditor` outside a
  // configured provider scope.
  if (!filterManager) {
    return null
  }
  const filter = filterManager.getFilterByColumnGeneratedId(columnTemplate.generatedId)

  const renderInputs = () => {
    switch (columnTemplate.dataType) {
      case 'Boolean':
        return (
          <>
            <Checkbox
              value={filter.bool === true}
              onValueChange={(val) => {
                runInAction(() => {
                  if (!val) {
                    filter.bool = undefined
                  }
                  if (val) {
                    filter.bool = true
                  }
                })
              }}
            >
              True
            </Checkbox>
            <Checkbox
              value={filter.bool === false}
              onValueChange={(val) => {
                runInAction(() => {
                  if (!val) {
                    filter.bool = undefined
                  }
                  if (val) {
                    filter.bool = false
                  }
                })
              }}
            >
              False
            </Checkbox>
          </>
        )
      case 'Number':
      case 'CurrencyCents':
        return <NumberFilter filter={filter} />
      case 'Date':
      case 'DateTime':
        return <DateFilter filter={filter} />
      default: {
        if (columnTemplate.isDateFieldColumn) {
          return <DateFilter filter={filter} />
        }
        if (columnTemplate.filterOptions.length === 0) {
          return (
            <EmptyState hideBorder size="sm">
              <EmptyState.Image />
              <EmptyState.Message>No options found</EmptyState.Message>
            </EmptyState>
          )
        }
        if (!isNil(filter.isNull)) {
          return null
        }
        const sortedOptions = computed(() =>
          columnTemplate.filterOptions
            .slice()
            .sort((a, b) =>
              filter
                .getOptionDisplayValue(a.identifier, paymentProviderFormatted)
                .localeCompare(filter.getOptionDisplayValue(b.identifier, paymentProviderFormatted))
            )
        ).get()
        const activeOptions = sortedOptions.filter((o) => !o.isArchived)
        const archivedOptions = sortedOptions.filter((o) => o.isArchived)
        const showArchivedOptions =
          isArchivedOptionsListOpen ||
          // automatically show archived options if any is selected
          archivedOptions.some((o) => !!filter.optionsSelected?.includes(o.identifier))
        return (
          <>
            {activeOptions.map((o) => (
              <OptionCheckbox key={o.identifier} option={o} filter={filter} />
            ))}
            {archivedOptions.length > 0 && (
              <>
                {!showArchivedOptions && (
                  <T.BodySmall>
                    <Link onClick={openArchivedOptionsList}>
                      {`Show archived options (${archivedOptions.length})`}
                    </Link>
                  </T.BodySmall>
                )}
                {showArchivedOptions && (
                  <>
                    <T.BodySmall className="mt-2 text-neutral-600">Archived Options</T.BodySmall>
                    {archivedOptions.map((o) => (
                      <OptionCheckbox key={o.identifier} option={o} filter={filter} />
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )
      }
    }
  }

  return (
    <StyledFlex vertical gap="6px">
      <Flex gap="6px">
        <Text colorToken="colorTextSecondary">{columnTemplate.labelSentenceCase}</Text>
        <FilterTypeSelect filterManager={filterManager} columnTemplate={columnTemplate} />
      </Flex>
      {renderInputs()}
    </StyledFlex>
  )
}

export default observer(FilterEditor)
